import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminUser, fetchUserCompanies } from '@/api/(admin)/users';

/** Shared by the route loader and the page, so the prefetch warms what the page reads. */
export function adminUserQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['admin', 'users', 'detail', id] as const,
    queryFn: () => fetchAdminUser(id),
  });
}

/**
 * The memberships are fetched separately from the user even though
 * `/admin/users/:id` already embeds `userCompanies`.
 *
 * Two reasons. The membership mutations (role, primary, add, remove) can then
 * invalidate just this key instead of refetching the whole user, and this is the
 * endpoint that stays authoritative afterwards — the embedded copy is a snapshot
 * from whenever the user was last loaded.
 */
export function userCompaniesQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ['admin', 'user-companies', userId] as const,
    queryFn: () => fetchUserCompanies(userId),
  });
}

export function useAdminUser(id: string) {
  return useQuery(adminUserQueryOptions(id));
}

export function useUserCompanies(userId: string) {
  const query = useQuery(userCompaniesQueryOptions(userId));
  return { ...query, companies: query.data?.data ?? [] };
}
